import { useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { getQuickDateRange } from '@/utils/reportDateRanges';
import type { AuditStatus, AuditTrailFilters, SortKey, SortDir } from '@/utils/AuditTrail/types';
import { toAuditTrailQuery } from '@/utils/AuditTrail/mappers';

export function useAuditTrailFilters() {
  // Filter state — "working" is edited in the filter sheet, "applied" drives the table
  const [appliedFilters, setAppliedFilters] = useState<AuditTrailFilters>({});
  const [workingFilters, setWorkingFilters] = useState<AuditTrailFilters>({});
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [quickDateRange, setQuickDateRange] = useState('all');

  // Table state
  const [sortKey, setSortKey] = useState<SortKey>('weekEnding');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebouncedValue(searchQuery.trim(), 300);

  const query = useMemo(
    () => toAuditTrailQuery(appliedFilters, debouncedSearch, sortKey, sortDir),
    [appliedFilters, debouncedSearch, sortKey, sortDir],
  );

  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const handleQuickDateChange = (value: string) => {
    setQuickDateRange(value);
    if (value === 'all') {
      setAppliedFilters((f) => { const next = { ...f }; delete next.dateFrom; delete next.dateTo; return next; });
    } else {
      setAppliedFilters((f) => ({ ...f, ...getQuickDateRange(value) }));
    }
    setPage(1);
  };

  const handleQuickStatusChange = (value: string) => {
    if (value === 'all') {
      setAppliedFilters((f) => { const next = { ...f }; delete next.statuses; return next; });
    } else {
      setAppliedFilters((f) => ({ ...f, statuses: [value as AuditStatus] }));
    }
    setPage(1);
  };

  // Apply sheet filters
  const handleApplySheet = () => {
    setAppliedFilters(workingFilters);
    setPage(1);
    setShowFilterSheet(false);
  };

  const handleClear = () => {
    setWorkingFilters({});
    setAppliedFilters({});
    setQuickDateRange('all');
    setSearchQuery('');
    setPage(1);
  };

  // Sort handler
  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const openFilterSheet = () => { setWorkingFilters(appliedFilters); setShowFilterSheet(true); };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.employeeIds?.length) count++;
    if (appliedFilters.dateFrom || appliedFilters.dateTo) count++;
    if (appliedFilters.statuses?.length) count++;
    if (appliedFilters.projectIds?.length) count++;
    if (appliedFilters.departments?.length) count++;
    if (appliedFilters.billableOnly) count++;
    if (appliedFilters.rejectedOnly) count++;
    return count;
  }, [appliedFilters]);

  // Count sheet-only filters (employee, dept, project, billable, rejected, custom dates)
  const sheetFilterCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.employeeIds?.length) count++;
    if (appliedFilters.departments?.length) count++;
    if (appliedFilters.projectIds?.length) count++;
    if (appliedFilters.billableOnly) count++;
    if (appliedFilters.rejectedOnly) count++;
    // custom date range (not one of the quick options)
    if ((appliedFilters.dateFrom || appliedFilters.dateTo) && quickDateRange === 'all') count++;
    return count;
  }, [appliedFilters, quickDateRange]);

  return {
    appliedFilters, setAppliedFilters, workingFilters, setWorkingFilters,
    showFilterSheet, setShowFilterSheet, searchQuery, setSearchQuery, quickDateRange,
    sortKey, sortDir, page, setPage, query,
    handleQuickDateChange, handleQuickStatusChange, handleApplySheet, handleClear, handleSort, openFilterSheet,
    activeFilterCount, sheetFilterCount,
  };
}

export type AuditTrailFiltersState = ReturnType<typeof useAuditTrailFilters>;
